import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, Shield, FileText } from "lucide-react";

// Privacy Policy Content
const PrivacyPolicyContent = ({ isSpanish }) => (
  <div className="space-y-6 text-primary/80 font-inter text-sm leading-relaxed">
    <p className="text-primary/60 italic">
      {isSpanish ? "Última actualización: Febrero 2025" : "Last updated: February 2025"}
    </p>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "1. Información que Recopilamos" : "1. Information We Collect"}
      </h3>
      <p>
        {isSpanish
          ? "Astral Belonging recopila la siguiente información para proporcionar lecturas astrológicas personalizadas:"
          : "Astral Belonging collects the following information to provide personalized astrological readings:"}
      </p>
      <ul className="list-disc list-inside mt-2 space-y-1 text-primary/70">
        <li>{isSpanish ? "Nombre (como deseas ser llamado)" : "Name (how you wish to be called)"}</li>
        <li>{isSpanish ? "Fecha, hora y lugar de nacimiento" : "Birth date, time, and location"}</li>
        <li>{isSpanish ? "Dirección de correo electrónico" : "Email address"}</li>
        <li>{isSpanish ? "Preferencias de idioma" : "Language preferences"}</li>
        <li>{isSpanish ? "Historial de conversaciones con los oráculos" : "Conversation history with oracles"}</li>
      </ul>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "2. Cómo Usamos Tu Información" : "2. How We Use Your Information"}
      </h3>
      <ul className="list-disc list-inside space-y-1 text-primary/70">
        <li>{isSpanish ? "Calcular tu carta natal y tránsitos planetarios" : "Calculate your birth chart and planetary transits"}</li>
        <li>{isSpanish ? "Proporcionar lecturas astrológicas personalizadas" : "Provide personalized astrological readings"}</li>
        <li>{isSpanish ? "Recordar tus preferencias entre sesiones" : "Remember your preferences between sessions"}</li>
        <li>{isSpanish ? "Enviar horóscopos diarios (si te suscribes)" : "Send daily horoscopes (if you subscribe)"}</li>
        <li>{isSpanish ? "Mejorar nuestros servicios" : "Improve our services"}</li>
      </ul>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "3. Compartir Datos" : "3. Data Sharing"}
      </h3>
      <p>
        {isSpanish
          ? "NO vendemos, intercambiamos ni transferimos tu información personal a terceros. Tus datos de nacimiento y conversaciones son privados y se utilizan únicamente para proporcionar servicios astrológicos."
          : "We do NOT sell, trade, or transfer your personal information to third parties. Your birth data and conversations are private and used solely to provide astrological services."}
      </p>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "4. Servicios de Terceros" : "4. Third-Party Services"}
      </h3>
      <p>{isSpanish ? "Utilizamos:" : "We use:"}</p>
      <ul className="list-disc list-inside mt-2 space-y-1 text-primary/70">
        <li>{isSpanish ? "Google Firebase para autenticación" : "Google Firebase for authentication"}</li>
        <li>{isSpanish ? "Google Gemini AI para generar lecturas" : "Google Gemini AI for generating readings"}</li>
        <li>{isSpanish ? "MongoDB Atlas para almacenamiento seguro de datos" : "MongoDB Atlas for secure data storage"}</li>
      </ul>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "5. Seguridad de Datos" : "5. Data Security"}
      </h3>
      <p>
        {isSpanish
          ? "Implementamos medidas de seguridad estándar de la industria, incluyendo encriptación, tokens de sesión seguros y almacenamiento seguro en la nube."
          : "We implement industry-standard security measures including encryption, secure session tokens, and secure cloud storage."}
      </p>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "6. Tus Derechos" : "6. Your Rights"}
      </h3>
      <p>{isSpanish ? "Tienes derecho a:" : "You have the right to:"}</p>
      <ul className="list-disc list-inside mt-2 space-y-1 text-primary/70">
        <li>{isSpanish ? "Acceder a tus datos personales" : "Access your personal data"}</li>
        <li>{isSpanish ? "Solicitar la eliminación de tu cuenta" : "Request deletion of your account"}</li>
        <li>{isSpanish ? "Exportar tus datos" : "Export your data"}</li>
        <li>{isSpanish ? "Optar por no recibir correos electrónicos" : "Opt out of email communications"}</li>
      </ul>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "7. Contacto" : "7. Contact"}
      </h3>
      <p>
        {isSpanish
          ? "Para preguntas sobre privacidad, contáctanos en:"
          : "For privacy questions, contact us at:"}
        <br />
        <a href="mailto:support@astralbelonging.com" className="text-primary underline">
          support@astralbelonging.com
        </a>
      </p>
    </section>
  </div>
);

// Terms of Service Content
const TermsOfServiceContent = ({ isSpanish }) => (
  <div className="space-y-6 text-primary/80 font-inter text-sm leading-relaxed">
    <p className="text-primary/60 italic">
      {isSpanish ? "Última actualización: Febrero 2025" : "Last updated: February 2025"}
    </p>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "1. Aceptación de Términos" : "1. Acceptance of Terms"}
      </h3>
      <p>
        {isSpanish
          ? "Al acceder y usar Astral Belonging, aceptas estar sujeto a estos Términos de Servicio. Si no estás de acuerdo con alguna parte de estos términos, no debes usar nuestros servicios."
          : "By accessing and using Astral Belonging, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not use our services."}
      </p>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "2. Descripción del Servicio" : "2. Service Description"}
      </h3>
      <p>
        {isSpanish
          ? "Astral Belonging proporciona lecturas astrológicas generadas por IA con fines de entretenimiento y autoexploración. Nuestros servicios incluyen:"
          : "Astral Belonging provides AI-generated astrological readings for entertainment and self-exploration purposes. Our services include:"}
      </p>
      <ul className="list-disc list-inside mt-2 space-y-1 text-primary/70">
        <li>{isSpanish ? "Cálculos de carta natal" : "Birth chart calculations"}</li>
        <li>{isSpanish ? "Horóscopos diarios" : "Daily horoscopes"}</li>
        <li>{isSpanish ? "Conversaciones con oráculos de IA" : "AI oracle conversations"}</li>
        <li>{isSpanish ? "Interpretación de sueños" : "Dream interpretation"}</li>
        <li>{isSpanish ? "Lecturas de tarot" : "Tarot readings"}</li>
      </ul>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "3. Aviso Legal Importante" : "3. Important Disclaimer"}
      </h3>
      <p className="text-primary/90 bg-primary/10 p-3 rounded-lg">
        {isSpanish
          ? "⚠️ Astral Belonging es solo para entretenimiento. Nuestras lecturas NO son sustitutos de consejos médicos, legales, financieros o psicológicos profesionales. Siempre consulta con profesionales calificados para asuntos serios de la vida."
          : "⚠️ Astral Belonging is for entertainment only. Our readings are NOT substitutes for professional medical, legal, financial, or psychological advice. Always consult qualified professionals for serious life matters."}
      </p>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "4. Cuentas de Usuario" : "4. User Accounts"}
      </h3>
      <ul className="list-disc list-inside space-y-1 text-primary/70">
        <li>{isSpanish ? "Debes proporcionar información precisa de nacimiento para lecturas exactas" : "You must provide accurate birth information for accurate readings"}</li>
        <li>{isSpanish ? "Eres responsable de mantener la seguridad de tu cuenta" : "You are responsible for maintaining your account security"}</li>
        <li>{isSpanish ? "Una cuenta por persona" : "One account per person"}</li>
        <li>{isSpanish ? "Debes tener al menos 13 años para usar nuestros servicios" : "You must be at least 13 years old to use our services"}</li>
      </ul>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "5. Uso Aceptable" : "5. Acceptable Use"}
      </h3>
      <p>{isSpanish ? "No debes:" : "You agree not to:"}</p>
      <ul className="list-disc list-inside mt-2 space-y-1 text-primary/70">
        <li>{isSpanish ? "Usar el servicio para cualquier propósito ilegal" : "Use the service for any illegal purpose"}</li>
        <li>{isSpanish ? "Intentar hackear o interrumpir nuestros sistemas" : "Attempt to hack or disrupt our systems"}</li>
        <li>{isSpanish ? "Crear múltiples cuentas para abusar de funciones gratuitas" : "Create multiple accounts to abuse free features"}</li>
        <li>{isSpanish ? "Revender o redistribuir nuestro contenido" : "Resell or redistribute our content"}</li>
      </ul>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "6. Propiedad Intelectual" : "6. Intellectual Property"}
      </h3>
      <p>
        {isSpanish
          ? "Todo el contenido, diseño e IA de Astral Belonging son propiedad de Astral Belonging. Puedes guardar tus lecturas personales pero no puedes reproducir o distribuir nuestro contenido comercialmente."
          : "All content, design, and AI of Astral Belonging are owned by Astral Belonging. You may save your personal readings but may not reproduce or distribute our content commercially."}
      </p>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "7. Cambios en el Servicio" : "7. Changes to Service"}
      </h3>
      <p>
        {isSpanish
          ? "Nos reservamos el derecho de modificar o discontinuar cualquier parte de nuestro servicio en cualquier momento. Te notificaremos sobre cambios significativos."
          : "We reserve the right to modify or discontinue any part of our service at any time. We will notify you of significant changes."}
      </p>
    </section>

    <section>
      <h3 className="text-primary font-cinzel text-lg mb-3">
        {isSpanish ? "8. Contacto" : "8. Contact"}
      </h3>
      <p>
        {isSpanish ? "Para preguntas sobre estos términos:" : "For questions about these terms:"}
        <br />
        <a href="mailto:support@astralbelonging.com" className="text-primary underline">
          support@astralbelonging.com
        </a>
      </p>
    </section>
  </div>
);

// Modal Component for Legal Pages
export const LegalModal = ({ isOpen, onClose, type, language = "en" }) => {
  const isSpanish = language?.toLowerCase().startsWith("es");
  
  const titles = {
    privacy: isSpanish ? "Política de Privacidad" : "Privacy Policy",
    terms: isSpanish ? "Términos de Servicio" : "Terms of Service"
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-2xl max-h-[85vh] bg-[#0a0a0f] border border-primary/30 rounded-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-[#0a0a0f] border-b border-primary/20">
            <div className="flex items-center gap-3">
              {type === "privacy" ? (
                <Shield className="w-5 h-5 text-primary" />
              ) : (
                <FileText className="w-5 h-5 text-primary" />
              )}
              <h2 className="font-cinzel text-xl text-primary">{titles[type]}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-primary/60 hover:text-primary transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
            {type === "privacy" ? (
              <PrivacyPolicyContent isSpanish={isSpanish} />
            ) : (
              <TermsOfServiceContent isSpanish={isSpanish} />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Small link component for footer/inline use
// Uses actual <a> tags pointing to static HTML files for SEO and Google OAuth verification crawling
export const LegalLinks = ({ language = "en", className = "" }) => {
  const isSpanish = language?.toLowerCase().startsWith("es");

  return (
    <div className={`flex items-center gap-3 text-xs text-primary/40 ${className}`}>
      <a
        href="/privacy.html"
        className="hover:text-primary/70 transition-colors underline"
      >
        {isSpanish ? "Privacidad" : "Privacy Policy"}
      </a>
      <span>•</span>
      <a
        href="/terms.html"
        className="hover:text-primary/70 transition-colors underline"
      >
        {isSpanish ? "Términos" : "Terms of Service"}
      </a>
    </div>
  );
};

export default LegalModal;
